import type { ZammadUserTicket } from "@/mocks/zammadTickets";
import type { ZammadTicketStats } from "@/mocks/zammadStats";
import type { TicketAttachment } from "@/mocks/ticketData";
import { decryptToken } from "@/utils/crypto";
import { isEmailWhitelisted } from "@/utils/whitelist";
import { isPublicCustomerCategory } from "@/config/zammadConfig";
import { getCurrentUser } from "@/services/entraIdService";

const getCleanBaseUrl = (): string => {
  // Siempre usar ruta relativa "" en DEV, QA y PROD para canalizar las peticiones
  // a través del proxy de Nginx (/api/v1/) o Vite Proxy, evitando errores CORS y 504.
  return "";
};

const ZAMMAD_BASE_URL = getCleanBaseUrl();
const ZAMMAD_TICKET_GROUP = (import.meta.env.VITE_ZAMMAD_TICKET_GROUP || "Users").trim();
const ZAMMAD_GENERAL_SERVICE = (
  import.meta.env.VITE_ZAMMAD_GENERAL_SERVICE || "Servicio de TI::Mesa de Ayuda"
).trim();
const ZAMMAD_CATEGORY_ATTRIBUTE = (
  import.meta.env.VITE_ZAMMAD_CATEGORY_ATTRIBUTE || "service"
).trim();

const EMPTY_STATS: ZammadTicketStats = {
  totalTickets: 0,
  openTickets: 0,
  inProgressTickets: 0,
  resolvedTickets: 0,
  closedTickets: 0,
  slaCompliance: 0,
  avgResolutionHours: 0,
  avgFirstResponseMinutes: 0,
  satisfactionAvg: 0,
  ticketsByCategory: [],
  ticketsByPriority: [],
  monthlyTrend: [],
};

const DEPT_GROUP_MAP: Record<string, string[]> = {
  ti: ["TI", "Mesa de Ayuda", "Soporte TI", "Tecnologia"],
  administracion: ["Administracion", "Administración", "Admin"],
  "bid-tecnologia": ["BID Tecnologia", "BID Tecnología", "BID Tech"],
  contraloria: ["Contraloria", "Contraloría"],
  "diseno-marketing": ["Diseno y Marketing", "Diseño y Marketing", "Marketing", "Diseno", "Diseño"],
  "servicios-generales": ["Servicios Generales"],
  "talento-humano": ["Talento Humano", "Recursos Humanos", "RH"],
  legal: ["Legal", "Juridico", "Jurídico"],
};

const SYSTEM_TAG_MAP: Record<string, string> = {
  "mesa-de-ayuda": "mesa_ayuda",
  "crm-hubspot": "crm_hubspot",
  "erp-netsuite": "erp_netsuite",
  "data-lake": "data_lake",
  bpm: "bpm",
  "solicitudes-sitio": "solicitudes_sitio",
  "talento-humano-ti": "talento_humano_ti",
  "adm-flotilla": "flotilla",
  "adm-viaticos": "viaticos",
  "adm-vuelos": "vuelos",
  "adm-proveedores": "proveedores",
  "adm-estacionamiento": "estacionamiento",
  "bid-nomisystem": "nomisystem",
  "bid-sharepoint": "sharepoint",
  "bid-silic": "silic",
  "bid-seiem-nomina": "seiem_nomina",
  "bid-seiem-infra": "seiem_infra",
  "ctr-cxc": "cxc",
  "ctr-cxp": "cxp",
  "ctr-fiscal": "fiscal",
  "ctr-auditoria": "auditoria",
  "dm-digitales": "digitales",
  "dm-impresos": "impresos",
  "dm-audiovisual": "audiovisual",
  "sg-mantenimiento": "mantenimiento",
  "sg-mensajeria": "mensajeria",
  "sg-papeleria": "papeleria",
  "sg-acceso": "acceso",
  "sg-proveedores": "proveedores_sg",
  "th-reclutamiento": "reclutamiento",
  "th-nomina": "nomina",
  "th-capacitacion": "capacitacion",
  "th-clima": "clima_laboral",
  "th-beneficios": "beneficios",
  "th-expedientes": "expedientes",
  "leg-contratos": "contratos",
  "leg-consultoria": "consultoria",
  "leg-gobierno": "gobierno_corp",
  "leg-litigio": "litigio",
  "leg-propiedad": "propiedad_intelectual",
  "leg-cumplimiento": "cumplimiento",
};

const CATEGORY_COLORS = [
  "bg-accent-500", "bg-primary-500", "bg-amber-500",
  "bg-emerald-500", "bg-secondary-500", "bg-rose-400", "bg-foreground-400",
];

const MONTH_NAMES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
const PORTAL_RATING_PREFERENCE = "portal_satisfaction_rating";
const PORTAL_RATING_MARKER_PREFIX = "PORTAL_RATING_";
const PORTAL_RATING_PATTERN = /\bPORTAL_RATING_([1-5])\b/i;

function getTicketSatisfactionRating(ticket: any): number | undefined {
  const preferenceRating = ticket?.preferences?.[PORTAL_RATING_PREFERENCE];
  const rawRating = preferenceRating ?? ticket?.satisfaction_rating;
  const value = typeof rawRating === "object" && rawRating !== null
    ? rawRating.value
    : rawRating;
  const numericValue = Number(value);

  return Number.isInteger(numericValue) && numericValue >= 1 && numericValue <= 5
    ? numericValue
    : undefined;
}

function getPortalRatingFromArticles(articles: any[]): number | undefined {
  for (let index = articles.length - 1; index >= 0; index--) {
    const match = String(articles[index]?.body || "").match(PORTAL_RATING_PATTERN);
    if (match) return Number(match[1]);
  }
  return undefined;
}

function removePortalRatingMarker(body: string): string {
  return body
    .replace(PORTAL_RATING_PATTERN, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function getTicketCategory(ticket: any): string {
  const category = ticket?.[ZAMMAD_CATEGORY_ATTRIBUTE];
  if (typeof category === "string") return category;
  return String(category?.value || category?.name || "");
}

interface ZammadUploadAttachment {
  filename: string;
  data: string;
  "mime-type": string;
}

async function fileToZammadAttachment(file: File): Promise<ZammadUploadAttachment> {
  const data = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      const separatorIndex = result.indexOf(",");
      if (separatorIndex < 0) {
        reject(new Error(`No se pudo procesar el archivo "${file.name}".`));
        return;
      }
      resolve(result.slice(separatorIndex + 1));
    };
    reader.onerror = () => reject(new Error(`No se pudo leer el archivo "${file.name}".`));
    reader.readAsDataURL(file);
  });

  return {
    filename: file.name,
    data,
    "mime-type": file.type || "application/octet-stream",
  };
}

async function filesToZammadAttachments(files?: File[]): Promise<ZammadUploadAttachment[]> {
  if (!files?.length) return [];
  return Promise.all(files.map(fileToZammadAttachment));
}

function mapArticleAttachments(article: any, ticketId: number): TicketAttachment[] {
  if (!Array.isArray(article?.attachments)) return [];

  return article.attachments
    .map((attachment: any) => ({
      id: Number(attachment?.id),
      articleId: Number(article?.id),
      ticketId,
      filename: String(attachment?.filename || "archivo"),
      size: Number(attachment?.size) || 0,
      mimeType: String(
        attachment?.preferences?.["Mime-Type"]
          || attachment?.preferences?.mime_type
          || attachment?.["mime-type"]
          || attachment?.content_type
          || "application/octet-stream"
      ),
    }))
    .filter(
      (attachment: TicketAttachment) =>
        Number.isInteger(attachment.id) &&
        Number.isInteger(attachment.articleId) &&
        Number.isInteger(attachment.ticketId)
    );
}

// ─── Cabeceras HTTP Dinámicas con Desencriptación ───
function getSystemHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  const rawToken = (import.meta.env.VITE_ZAMMAD_SYSTEM_TOKEN || import.meta.env.VITE_ZAMMAD_TOKEN || "").trim();
  if (rawToken) {
    const decrypted = decryptToken(rawToken) || rawToken;
    if (decrypted.startsWith("Token ") || decrypted.startsWith("Bearer ") || decrypted.startsWith("Basic ")) {
      headers["Authorization"] = decrypted;
    } else {
      headers["Authorization"] = `Token token=${decrypted}`;
    }
  }

  return headers;
}

function getStoredUserHeaders(): Record<string, string> | null {
  const encryptedToken = localStorage.getItem("zammad_auth_token");
  if (encryptedToken) {
    const decrypted = decryptToken(encryptedToken);
    if (
      decrypted &&
      decrypted !== "zammad_local_session" &&
      (decrypted.startsWith("Basic ") || decrypted.startsWith("Token "))
    ) {
      return {
        "Content-Type": "application/json",
        Authorization: decrypted,
      };
    }
  }

  return null;
}

// ─── Cabeceras HTTP Dinámicas con Desencriptación ───
function getHeaders(): Record<string, string> {
  return getStoredUserHeaders() || getSystemHeaders();
}

// ─── Helpers de paginación con Autorrecuperación en 401 ───
async function fetchAllPages(url: string, headers: Record<string, string>): Promise<any[]> {
  const results: any[] = [];
  let page = 1;
  const perPage = 100;

  let activeHeaders = headers;

  while (true) {
    const separator = url.includes("?") ? "&" : "?";
    const pageUrl = `${url}${separator}page=${page}&per_page=${perPage}`;

    let res = await fetch(pageUrl, { headers: activeHeaders });

    if (res.status === 401 || res.status === 422) {
      // Si la sesión guardada expiró o es rechazada por Zammad, limpiar localStorage y reintentar con el token de sistema
      localStorage.removeItem("zammad_auth_token");
      localStorage.removeItem("zammad_auth_user");
      activeHeaders = getSystemHeaders();
      res = await fetch(pageUrl, { headers: activeHeaders });
    }

    if (!res.ok) {
      console.error(`Zammad API error: ${res.status} at ${pageUrl}`);
      throw new Error(`Zammad no pudo responder la consulta (HTTP ${res.status}).`);
    }

    const data = await res.json();
    if (!Array.isArray(data)) break;
    results.push(...data);

    const totalHeader = res.headers.get("X-Total");
    if (totalHeader) {
      const total = parseInt(totalHeader, 10);
      if (!Number.isFinite(total) || page * perPage >= total) break;
    } else if (data.length < perPage) {
      break;
    }
    page++;
  }

  return results;
}

async function fetchTicketsByQuery(query: string, headers: Record<string, string>): Promise<any[]> {
  const url = `${ZAMMAD_BASE_URL}/api/v1/tickets/search?query=${encodeURIComponent(query)}&expand=true`;
  return await fetchAllPages(url, headers);
}

// Cache local de grupos
let groupsCache: any[] = [];
let groupsLastFetched = 0;
let groupsRequest: Promise<any[]> | null = null;

async function getGroups(headers: Record<string, string>): Promise<any[]> {
  const now = Date.now();
  if (groupsCache.length > 0 && now - groupsLastFetched < 300000) {
    return groupsCache;
  }
  if (groupsRequest) return groupsRequest;

  groupsRequest = (async () => {
    let activeHeaders = headers;
    let res = await fetch(`${ZAMMAD_BASE_URL}/api/v1/groups`, { headers: activeHeaders });

    if (res.status === 401 || res.status === 422) {
      localStorage.removeItem("zammad_auth_token");
      localStorage.removeItem("zammad_auth_user");
      activeHeaders = getSystemHeaders();
      res = await fetch(`${ZAMMAD_BASE_URL}/api/v1/groups`, { headers: activeHeaders });
    }

    if (!res.ok) return [];
    try {
      const data = await res.json();
      groupsCache = data;
      groupsLastFetched = Date.now();
      return groupsCache;
    } catch {
      return [];
    }
  })().finally(() => {
    groupsRequest = null;
  });

  return groupsRequest;
}

const CUSTOMER_TICKETS_CACHE_TTL = 60 * 1000;
const customerTicketsCache = new Map<number, { data: any[]; fetchedAt: number }>();
const customerTicketsRequests = new Map<number, Promise<any[]>>();

async function fetchPortalRatingsAsCustomer(
  customerId: number,
  headers: Record<string, string>
): Promise<Map<number, number>> {
  const ratings = new Map<number, number>();
  try {
    // 5 consultas fijas acotadas por customer_id para saber exactamente la calificación de cada ticket (1..5)
    // Esto evita por completo hacer N peticiones a ticket_articles por cada ticket del usuario.
    const resultsByRating = await Promise.all(
      [1, 2, 3, 4, 5].map(async (rating) => ({
        rating,
        tickets: await fetchTicketsByQuery(`PORTAL_RATING_${rating} AND customer_id:${customerId}`, headers),
      }))
    );

    for (const { rating, tickets } of resultsByRating) {
      for (const ticket of tickets) {
        if (Number(ticket.customer_id) === customerId) {
          ratings.set(Number(ticket.id), rating);
        }
      }
    }
  } catch (err) {
    console.error("Error fetching portal ratings:", err);
  }
  return ratings;
}

async function fetchTicketsAsCustomer(customerId: number, forceRefresh = false): Promise<any[]> {
  const cached = customerTicketsCache.get(customerId);
  if (!forceRefresh && cached && Date.now() - cached.fetchedAt < CUSTOMER_TICKETS_CACHE_TTL) {
    return cached.data;
  }
  if (!forceRefresh) {
    const pending = customerTicketsRequests.get(customerId);
    if (pending) return pending;
  }

  const headers = {
    ...getSystemHeaders(),
    From: String(customerId),
  };

  const request = Promise.all([
    // Filtrar directamente en Zammad por customer_id para no traer tickets de otros usuarios
    fetchAllPages(`${ZAMMAD_BASE_URL}/api/v1/tickets?expand=true&customer_id=${customerId}`, headers),
    fetchPortalRatingsAsCustomer(customerId, headers),
  ])
    .then(([tickets, ratings]) => {
      const data = tickets.map((ticket) => {
        const portalRating = ratings.get(Number(ticket.id));
        return portalRating === undefined
          ? ticket
          : { ...ticket, satisfaction_rating: portalRating };
      });
      customerTicketsCache.set(customerId, { data, fetchedAt: Date.now() });
      return data;
    })
    .finally(() => {
      if (customerTicketsRequests.get(customerId) === request) {
        customerTicketsRequests.delete(customerId);
      }
    });

  customerTicketsRequests.set(customerId, request);
  return request;
}

// ─── Mapeos auxiliares ───
type TicketStatus = "open" | "in_progress" | "resolved" | "closed";

function normalizeStateName(state: unknown): string {
  const stateObject = state && typeof state === "object"
    ? state as { name?: unknown; title?: unknown; label?: unknown }
    : undefined;
  const name = typeof state === "string"
    ? state
    : String(stateObject?.name || stateObject?.title || stateObject?.label || "");

  return name
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

function mapState(stateId: number, state?: unknown): TicketStatus {
  const stateName = normalizeStateName(state);

  // "06 Resuelto" es una etiqueta personalizada; su prefijo no es el state_id.
  if (/\b(resuelto|resolved)\b/.test(stateName)) return "resolved";

  const map: Record<number, TicketStatus> = {
    1: "open",
    2: "open",
    3: "in_progress",
    7: "in_progress",
    8: "in_progress",
    4: "closed",
    5: "closed",
  };
  return map[stateId] || "open";
}

function mapPriority(priorityId: number): "low" | "medium" | "high" | "critical" {
  const map: Record<number, "low" | "medium" | "high" | "critical"> = {
    1: "low",
    2: "medium",
    3: "high",
    4: "critical",
  };
  return map[priorityId] || "medium";
}

function getGroupIdsForDept(groups: any[], dept: string): number[] {
  const names = DEPT_GROUP_MAP[dept] || [];
  if (names.length === 0) return [];
  return groups
    .filter((g) => names.some((n) => g.name.toLowerCase().includes(n.toLowerCase())))
    .map((g) => g.id);
}

function getZammadGroupName(deptIdOrName: string, allGroups: any[]): string {
  if (!allGroups || allGroups.length === 0) return "Mesa de Ayuda";
  const lowerInput = deptIdOrName.toLowerCase().trim();

  const directMatch = allGroups.find(g => g.active && g.name.toLowerCase() === lowerInput);
  if (directMatch) return directMatch.name;

  if (lowerInput.includes("::")) {
    const parts = lowerInput.split("::");
    const lastPart = parts[parts.length - 1].trim();
    const parentPath = parts.slice(0, -1).join("::").trim();
    const keywords = lastPart
      .split(/\s+/)
      .filter(w => w.length > 2 && !["del", "con", "los", "las", "sus", "por", "para", "una", "uno"].includes(w));

    for (const word of keywords) {
      const wordMatch = allGroups.find(g =>
        g.active &&
        g.name.toLowerCase().includes(parentPath) &&
        g.name.toLowerCase().includes(word)
      );
      if (wordMatch) return wordMatch.name;
    }

    for (let depth = parts.length - 1; depth >= 1; depth--) {
      const candidate = parts.slice(0, depth).join("::").toLowerCase().trim();
      const parentMatch = allGroups.find(g => g.active && g.name.toLowerCase() === candidate);
      if (parentMatch) return parentMatch.name;
    }
  }

  if (lowerInput.includes("netsuite") || lowerInput.includes("erp")) {
    const g = allGroups.find(x => x.active && x.name.includes("ERP (NetSuite)"));
    if (g) return g.name;
  }
  if (lowerInput.includes("hubspot") || lowerInput.includes("crm")) {
    const g = allGroups.find(x => x.active && x.name.includes("CRM (HubSpot)"));
    if (g) return g.name;
  }
  if (lowerInput.includes("talento") || lowerInput.includes("humano") || lowerInput.includes("rh")) {
    const g = allGroups.find(x => x.active && x.name === "Servicio de TI::Talento Humano");
    if (g) return g.name;
  }
  if (lowerInput.includes("sitio") || lowerInput.includes("solicitudes en sitio")) {
    const g = allGroups.find(x => x.active && x.name.includes("Solicitudes en Sitio"));
    if (g) return g.name;
  }
  if (lowerInput.includes("ayuda") || lowerInput.includes("mesa de ayuda") || lowerInput.includes("soporte") || lowerInput === "ti" || lowerInput === "mesa de servicios ti") {
    const g = allGroups.find(x => x.active && x.name.includes("Mesa de Ayuda"));
    if (g) return g.name;
  }
  if (lowerInput.includes("tecnología") || lowerInput.includes("tecnologia") || lowerInput === "servicio de ti") {
    const g = allGroups.find(x => x.active && x.name.includes("Servicio de TI"));
    if (g) return g.name;
  }
  if (lowerInput.includes("contralor")) {
    const g = allGroups.find(x => x.active && x.name === "Contraloría");
    if (g) return g.name;
  }
  if (lowerInput.includes("diseño") || lowerInput.includes("diseno") || lowerInput.includes("marketing")) {
    const g = allGroups.find(x => x.active && x.name === "Diseño y Marketing");
    if (g) return g.name;
  }
  if (lowerInput.includes("legal") || lowerInput.includes("área legal") || lowerInput.includes("area legal")) {
    const g = allGroups.find(x => x.active && x.name.toLowerCase().includes("legal"));
    if (g) return g.name;
  }
  if (lowerInput.includes("servicios generales") || lowerInput.includes("generales")) {
    const g = allGroups.find(x => x.active && x.name.toLowerCase().includes("generales"));
    if (g) return g.name;
  }

  for (const group of allGroups) {
    if (!group.active) continue;
    const words = lowerInput.split(/\s+/);
    for (const word of words) {
      if (word.length > 3) {
        const regex = new RegExp(`\\b${word}\\b`, "i");
        if (regex.test(group.name)) {
          return group.name;
        }
      }
    }
  }

  const firstActive = allGroups.find(g => g.active);
  return firstActive ? firstActive.name : "Mesa de Ayuda";
}

// ─── API PÚBLICA / EXPORTADA ───

export async function fetchLiveStats(options?: {
  dept?: string;
  system?: string;
  userId?: string;
  email?: string;
}): Promise<ZammadTicketStats> {
  try {
    if (!options?.userId && !options?.email) return EMPTY_STATS;

    const headers = getSystemHeaders();
    const numericUserId = Number(options.userId);
    let customerId = Number.isInteger(numericUserId) && numericUserId > 0
      ? numericUserId
      : undefined;
    if (!customerId && options.email) {
      customerId = await resolveCustomerId(options.email, headers);
    }
    if (!customerId) return EMPTY_STATS;

    const allGroups = await getGroups(headers);
    let groupIds: number[] = [];
    let baseFilter = "";

    if (options?.dept && options.dept !== "global") {
      groupIds = getGroupIdsForDept(allGroups, options.dept);
    }

    if (options?.system && options.system !== "all") {
      const tag = SYSTEM_TAG_MAP[options.system];
      if (tag) {
        baseFilter = `tags:${tag}`;
      }
    }

    // Usar exclusivamente los tickets donde el usuario es el solicitante.
    const visibleTickets = await fetchTicketsAsCustomer(customerId);
    const tickets = visibleTickets.filter(
      (ticket) => Number(ticket.customer_id) === customerId
    );

    // Filtrado en memoria
    const filteredTickets = tickets.filter((t) => {
      if (groupIds.length > 0 && !groupIds.includes(t.group_id)) {
        return false;
      }
      if (baseFilter.startsWith("tags:")) {
        const tag = baseFilter.replace("tags:", "");
        const tags = t.tag_list;
        if (!tags || !tags.includes(tag)) {
          return false;
        }
      }
      return true;
    });

    const totalTickets = filteredTickets.length;
    let openTickets = 0;
    let inProgressTickets = 0;
    let resolvedTickets = 0;
    let closedTickets = 0;

    let lowPrio = 0;
    let mediumPrio = 0;
    let highPrio = 0;
    let criticalPrio = 0;

    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

    let slaMet = 0;
    let totalResolutionMinutes = 0;
    let totalFirstResponseMinutes = 0;
    let totalSatisfaction = 0;
    let satisfactionCount = 0;
    let responseCount = 0;
    let resolutionCount = 0;

    const monthlyTrendMap: Record<string, { created: number; resolved: number }> = {};
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      monthlyTrendMap[MONTH_NAMES[d.getMonth()]] = { created: 0, resolved: 0 };
    }

    for (const t of filteredTickets) {
      const status = mapState(t.state_id, t.state);
      if (status === "open") openTickets++;
      else if (status === "in_progress") inProgressTickets++;
      else if (status === "resolved") resolvedTickets++;
      else if (status === "closed") closedTickets++;

      if (t.priority_id === 1) lowPrio++;
      else if (t.priority_id === 2) mediumPrio++;
      else if (t.priority_id === 3) highPrio++;
      else if (t.priority_id === 4) criticalPrio++;

      const created = new Date(t.created_at);
      const closed = t.close_at ? new Date(t.close_at) : null;
      const firstResp = t.first_response_at ? new Date(t.first_response_at) : null;

      if (status === "resolved" || status === "closed") {
        if (closed && created >= sixMonthsAgo) {
          const resolutionMin = (closed.getTime() - created.getTime()) / 60000;
          totalResolutionMinutes += resolutionMin;
          resolutionCount++;
          if (resolutionMin <= 480) slaMet++;
        }

        const ratingValue = getTicketSatisfactionRating(t);
        if (ratingValue !== undefined) {
          totalSatisfaction += ratingValue;
          satisfactionCount++;
        }
      }

      if (firstResp) {
        const respMin = (firstResp.getTime() - created.getTime()) / 60000;
        totalFirstResponseMinutes += respMin;
        responseCount++;
      }

      const createdMonth = MONTH_NAMES[created.getMonth()];
      if (monthlyTrendMap[createdMonth]) {
        monthlyTrendMap[createdMonth].created++;
      }
      if (closed && (status === "resolved" || status === "closed")) {
        const closedMonth = MONTH_NAMES[closed.getMonth()];
        if (monthlyTrendMap[closedMonth]) {
          monthlyTrendMap[closedMonth].resolved++;
        }
      }
    }

    const slaCompliance = resolutionCount > 0 ? Math.round((slaMet / resolutionCount) * 1000) / 10 : 0;
    const avgResolutionHours = resolutionCount > 0 ? Math.round((totalResolutionMinutes / resolutionCount / 60) * 10) / 10 : 0;
    const avgFirstResponseMinutes = responseCount > 0 ? Math.round(totalFirstResponseMinutes / responseCount) : 0;
    const satisfactionAvg = satisfactionCount > 0 ? Math.round((totalSatisfaction / satisfactionCount) * 10) / 10 : 0;

    const monthlyTrend = Object.keys(monthlyTrendMap).map((mName) => ({
      month: mName,
      created: monthlyTrendMap[mName].created,
      resolved: monthlyTrendMap[mName].resolved,
    }));

    const groupTickets: { name: string; count: number }[] = [];
    if (groupIds.length > 1) {
      for (const gid of groupIds) {
        const count = filteredTickets.filter((t) => t.group_id === gid).length;
        const group = allGroups.find((g) => g.id === gid);
        groupTickets.push({ name: group?.name || `Grupo ${gid}`, count });
      }
    } else {
      const tagCounts: Record<string, number> = {};
      for (const t of filteredTickets) {
        const tags = t.tag_list;
        if (tags) {
          for (const tag of tags) {
            tagCounts[tag] = (tagCounts[tag] || 0) + 1;
          }
        }
      }
      const sortedTags = Object.entries(tagCounts)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 7);

      if (sortedTags.length > 0) {
        groupTickets.push(...sortedTags.map(([name, count]) => ({ name, count })));
      }
    }

    const ticketsByCategory = groupTickets.length > 0
      ? groupTickets.map((c, i) => ({
          name: c.name,
          count: c.count,
          color: CATEGORY_COLORS[i % CATEGORY_COLORS.length],
        }))
      : [{ name: "General", count: totalTickets, color: CATEGORY_COLORS[0] }];

    const ticketsByPriority = [
      { name: "Crítica", count: criticalPrio, color: "bg-red-500" },
      { name: "Alta", count: highPrio, color: "bg-amber-500" },
      { name: "Media", count: mediumPrio, color: "bg-accent-500" },
      { name: "Baja", count: lowPrio, color: "bg-foreground-400" },
    ];

    // Palabras comunes
    const wordCounts: Record<string, number> = {};
    const STOP_WORDS = new Set([
      "de", "la", "el", "en", "y", "a", "los", "del", "se", "las", "por", "un", "para", "con", "no", 
      "una", "su", "al", "lo", "como", "más", "pero", "sus", "este", "o", "mi", "esta", "si", "error", 
      "ticket", "tickets", "problema", "soporte", "solicitud", "reporte", "ayuda", "falla", "sistema",
      "acceso", "cuenta", "usuario", "solicitar", "servicio", "crear", "nuevo", "nueva", "cambio", 
      "alta", "baja", "reportar", "incidente", "general"
    ]);

    for (const t of filteredTickets) {
      const title = t.title || "";
      const words = title
        .toLowerCase()
        .replace(/[áéíóúü]/g, (match) => {
          const map: Record<string, string> = { á: "a", é: "e", í: "i", ó: "o", ú: "u", ü: "u" };
          return map[match] || match;
        })
        .replace(/[^a-z0-9\s]/g, "")
        .split(/\s+/)
        .filter(w => w.length > 2 && !STOP_WORDS.has(w));
      
      for (const w of words) {
        wordCounts[w] = (wordCounts[w] || 0) + 1;
      }
    }

    const commonWords = Object.entries(wordCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5)
      .map(([word]) => word);

    return {
      totalTickets,
      openTickets,
      inProgressTickets,
      resolvedTickets,
      closedTickets,
      slaCompliance,
      avgResolutionHours,
      avgFirstResponseMinutes,
      satisfactionAvg,
      ticketsByCategory,
      ticketsByPriority,
      monthlyTrend,
      commonWords
    };
  } catch (err) {
    console.error("Fetch live stats failed, returning empty stats:", err);
    return EMPTY_STATS;
  }
}

type MyTicketsOptions = {
  userId?: string;
  email?: string;
  forceRefresh?: boolean;
};

const MY_TICKETS_CACHE_TTL = 60 * 1000;
const myTicketsCache = new Map<string, { data: ZammadUserTicket[]; fetchedAt: number }>();
const myTicketsRequests = new Map<string, Promise<ZammadUserTicket[]>>();

function invalidateMyTicketsCache(): void {
  myTicketsCache.clear();
  customerTicketsCache.clear();
}

function getGroupNameSimilarity(left: string, right: string): number {
  const leftWords = new Set(normalizeZammadPath(left).split(/[^a-z0-9]+/).filter(Boolean));
  const rightWords = new Set(normalizeZammadPath(right).split(/[^a-z0-9]+/).filter(Boolean));
  if (leftWords.size === 0 || rightWords.size === 0) return 0;

  let sharedWords = 0;
  for (const word of leftWords) {
    if (rightWords.has(word)) sharedWords += 1;
  }

  return (2 * sharedWords) / (leftWords.size + rightWords.size);
}

function findSimilarGroupName(
  groups: any[],
  requestedPath: string
): string | undefined {
  const requestedVariants = getZammadPathVariants(requestedPath);
  let bestMatch: { name: string; score: number } | undefined;

  for (const group of groups) {
    if (group?.active === false || !group?.name) continue;

    const groupParts = normalizeZammadPath(group.name).split("::").filter(Boolean);

    for (const variant of requestedVariants) {
      const requestedParts = variant.split("::").filter(Boolean);
      if (groupParts.length !== requestedParts.length) continue;

      const requestedParent = requestedParts.slice(0, -1).join("::");
      const groupParent = groupParts.slice(0, -1).join("::");
      if (requestedParent !== groupParent) continue;

      const requestedLeaf = requestedParts.at(-1) || "";
      const groupLeaf = groupParts.at(-1) || "";
      const containsLeaf = requestedLeaf.length >= 4
        && (requestedLeaf.includes(groupLeaf) || groupLeaf.includes(requestedLeaf));
      const score = containsLeaf ? 1 : getGroupNameSimilarity(requestedLeaf, groupLeaf);

      if (score >= 0.6 && (!bestMatch || score > bestMatch.score)) {
        bestMatch = { name: group.name, score };
      }
    }
  }

  return bestMatch?.name;
}

async function resolveTicketGroupCandidates(
  service: string,
  headers: Record<string, string>
): Promise<string[]> {
  const groups = await getGroups(headers);
  const activeGroups = groups.filter((group) => group?.active !== false && group?.name);
  const pathParts = service.split("::").map((part) => part.trim()).filter(Boolean);
  const candidates: string[] = [];

  const addCandidate = (name?: string) => {
    if (name && !candidates.some(
      (candidate) => normalizeZammadPath(candidate) === normalizeZammadPath(name)
    )) {
      candidates.push(name);
    }
  };

  // En cada nivel se intenta primero la ruta exacta, después una variante
  // suficientemente similar y finalmente se sube al padre.
  while (pathParts.length > 0) {
    const requestedPath = pathParts.join("::");
    const variants = getZammadPathVariants(requestedPath);
    const exactGroup = activeGroups.find(
      (group) => variants.includes(normalizeZammadPath(group.name))
    );
    addCandidate(exactGroup?.name);
    addCandidate(findSimilarGroupName(activeGroups, requestedPath));
    pathParts.pop();
  }

  const normalizedGeneral = normalizeZammadPath(ZAMMAD_TICKET_GROUP);
  const configuredGeneral = activeGroups.find(
    (group) => normalizeZammadPath(group.name) === normalizedGeneral
  );
  addCandidate(configuredGeneral?.name || ZAMMAD_TICKET_GROUP);

  return candidates;
}

async function loadLiveMyTickets(options?: MyTicketsOptions): Promise<ZammadUserTicket[]> {
  try {
    const systemHeaders = getSystemHeaders();
    if (!options?.userId && !options?.email) {
      return [];
    }

    // Resolver correo electrónico (prioritario) ya sea de options.email o de options.userId (si es que contiene @)
    let email = options.email || "";
    if (!email && options.userId && options.userId.includes("@")) {
      email = options.userId;
    }

    const numericUserId = Number(options.userId);
    let customerId = Number.isInteger(numericUserId) && numericUserId > 0
      ? numericUserId
      : undefined;

    if (!customerId && email) {
      customerId = await resolveCustomerId(email, systemHeaders);
    }

    if (!customerId) return [];

    // Ejecutar la consulta en nombre del usuario fuerza el mismo alcance que
    // su vista Customer, aunque la cuenta tambien tenga el rol Agent. El
    // filtro elimina los tickets visibles solo por permisos de agente.
    const visibleTickets = await fetchTicketsAsCustomer(customerId, options.forceRefresh);
    // La API ya filtra por customer_id — el filter en memoria es solo como red de seguridad
    const tickets = visibleTickets.filter(
      (ticket) => Number(ticket.customer_id) === customerId
    );
    const needsGroupCatalog = tickets.some((ticket) => !ticket.group && !ticket.group_name);
    const allGroups = needsGroupCatalog ? await getGroups(systemHeaders) : [];

    return tickets.map((t) => {
      const satRating = getTicketSatisfactionRating(t);

      return {
        id: String(t.number),
        zammadId: t.id,
        title: t.title,
        category: getTicketCategory(t)
          || (typeof t.group === "string" ? t.group : t.group?.name)
          || t.group_name
          || allGroups.find((g) => g.id === t.group_id)?.name
          || "",
        subcategory: "",
        status: mapState(t.state_id, t.state),
        priority: mapPriority(t.priority_id),
        createdAt: t.created_at,
        updatedAt: t.updated_at,
        description: "",
        assignedTo: "Mesa de Ayuda",
        userId: String(t.customer_id),
        userEmail: "",
        lastSyncedAt: new Date().toISOString(),
        slaBreached: false,
        satisfactionRating: satRating,
        timeline: [],
      };
    });
  } catch (err) {
    console.error("Fetch live tickets failed:", err);
    throw err;
  }
}

export async function fetchTicketDetail(
  ticketId: string,
  customerIdentity?: string
): Promise<ZammadUserTicket> {
  try {
    const headers = {
      ...getSystemHeaders(),
      ...(customerIdentity ? { From: customerIdentity } : {}),
    };
    // Acotar la búsqueda al usuario logueado si se conoce su identidad
    const customerSuffix = customerIdentity && !customerIdentity.includes('@') ? ` AND customer_id:${customerIdentity}` : "";
    const query = `number:${ticketId}${customerSuffix}`;
    const tickets = await fetchTicketsByQuery(query, headers);
    if (tickets.length === 0) {
      throw new Error("Ticket not found");
    }

    const t = tickets[0];
    const allGroups = await getGroups(headers);
    
    let description = "Sin descripción adicional.";
    let articleRating: number | undefined;
    const timeline: {
      date: string;
      title: string;
      description: string;
      icon: string;
      attachments?: TicketAttachment[];
    }[] = [];
    
    try {
      const resArticles = await fetch(`${ZAMMAD_BASE_URL}/api/v1/ticket_articles/by_ticket/${t.id}`, { headers });
      if (resArticles.ok) {
        const articles = await resArticles.json();
        if (Array.isArray(articles) && articles.length > 0) {
          description = articles[0].body || t.title;
          articleRating = getPortalRatingFromArticles(articles);
          
          articles.forEach((art: any, index: number) => {
            let icon = "ri-chat-3-line";
            let eventTitle = "Actualización de ticket";
            if (index === 0) {
              icon = "ri-add-circle-line";
              eventTitle = "Ticket creado";
            } else if (art.internal) {
              icon = "ri-lock-line";
              eventTitle = "Nota interna";
            } else {
              icon = "ri-user-voice-line";
              eventTitle = art.sender === "Customer" ? "Respuesta de cliente" : "Respuesta de agente";
            }
            
            const bodyText = removePortalRatingMarker(
              String(art.body || "").replace(/<[^>]*>/g, "")
            );
            
            timeline.push({
              date: art.created_at,
              title: eventTitle,
              description: bodyText.slice(0, 200) + (bodyText.length > 200 ? "..." : ""),
              icon: icon,
              attachments: mapArticleAttachments(art, Number(t.id)),
            });
          });
        }
      }
    } catch (e) {
      console.error("Failed to fetch ticket articles in client:", e);
    }

    if (timeline.length === 0) {
      timeline.push({
        date: t.created_at,
        title: "Ticket creado",
        description: "El ticket fue registrado en el sistema",
        icon: "ri-add-circle-line",
      });
    }

    const satRating = articleRating ?? getTicketSatisfactionRating(t);

    return {
      id: String(t.number),
      zammadId: t.id,
      title: t.title,
      category: getTicketCategory(t)
        || allGroups.find((g) => g.id === t.group_id)?.name
        || "",
      subcategory: "",
      status: mapState(t.state_id, t.state),
      priority: mapPriority(t.priority_id),
      createdAt: t.created_at,
      updatedAt: t.updated_at,
      description: description,
      assignedTo: "Mesa de Ayuda",
      userId: String(t.customer_id),
      userEmail: "",
      lastSyncedAt: new Date().toISOString(),
      slaBreached: false,
      satisfactionRating: satRating,
      timeline: timeline,
    };
  } catch (err) {
    console.error(`Fetch ticket detail failed for ${ticketId}:`, err);
    throw err;
  }
}

function normalizeZammadPath(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s*::\s*/g, "::")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

const ZAMMAD_ROOT_ALIASES: Record<string, string> = {
  administracion: "servicio de administracion",
  "servicio de administracion": "servicio de administracion",
  "servicios generales": "servicio de administracion",
  contraloria: "servicio de contraloria",
  "servicio de contraloria": "servicio de contraloria",
  "diseno y marketing": "servicio de diseno y marketing",
  "servicio de diseno y marketing": "servicio de diseno y marketing",
  "centro de excelencia": "servicio de ti::centro de excelencia",
  "sharepoint bid tecnologia": "servicio de ti::sharepoint bid tecnologia",
};

function getZammadPathVariants(value: string): string[] {
  const requested = normalizeZammadPath(value);
  const requestedParts = requested.split("::").filter(Boolean);
  const variants = new Set<string>([requested]);
  const aliasedRoot = ZAMMAD_ROOT_ALIASES[requestedParts[0] || ""];

  if (aliasedRoot) {
    variants.add([aliasedRoot, ...requestedParts.slice(1)].join("::"));
  }

  return [...variants];
}

function findValidServiceValue(
  nodes: ZammadCategoryTreeNode[],
  requestedService?: string
): string | undefined {
  if (!requestedService?.trim()) return undefined;
  const variants = getZammadPathVariants(requestedService);

  function findInTree(currentNodes: ZammadCategoryTreeNode[]): string | undefined {
    for (const node of currentNodes) {
      const value = (node.value || node.name || "").trim();
      if (variants.includes(normalizeZammadPath(value))) {
        return value;
      }

      const childMatch = findInTree(node.children || []);
      if (childMatch) return childMatch;
    }

    return undefined;
  }

  return findInTree(nodes);
}

function getServiceAncestorValues(
  nodes: ZammadCategoryTreeNode[],
  service: string
): string[] {
  const pathParts = service.split("::").map((part) => part.trim()).filter(Boolean);
  const ancestors: string[] = [];

  while (pathParts.length > 1) {
    pathParts.pop();
    const ancestor = findValidServiceValue(nodes, pathParts.join("::"));
    if (ancestor && !ancestors.includes(ancestor)) ancestors.push(ancestor);
  }

  return ancestors;
}

function isInvalidServiceValueError(status: number, responseText: string): boolean {
  return status === 422
    && /invalid value[\s\S]*for field ['\"]service['\"]/i.test(responseText);
}

function isRetryableGroupError(status: number, responseText: string): boolean {
  return status === 403
    || (
      status === 422
      && /(?:invalid value|required value)[\s\S]*for field ['\"](?:group|group_id)['\"]/i.test(responseText)
    );
}

const customerIdCache = new Map<string, number>();

async function resolveCustomerId(
  email: string,
  headers: Record<string, string>
): Promise<number | undefined> {
  const normalizedEmail = email.trim().toLowerCase();
  const cachedId = customerIdCache.get(normalizedEmail);
  if (cachedId) return cachedId;

  try {
    const response = await fetch(
      `${ZAMMAD_BASE_URL}/api/v1/users/search?query=${encodeURIComponent(normalizedEmail)}&limit=10`,
      { headers }
    );
    if (!response.ok) return undefined;

    const data = await response.json();
    const users = Array.isArray(data) ? data : [];
    const customer = users.find((candidate) => {
      const login = String(candidate?.login || "").trim().toLowerCase();
      const candidateEmail = String(candidate?.email || "").trim().toLowerCase();
      return candidate?.active !== false && (login === normalizedEmail || candidateEmail === normalizedEmail);
    });

    const customerId = Number(customer?.id);
    if (Number.isInteger(customerId) && customerId > 0) {
      customerIdCache.set(normalizedEmail, customerId);
      return customerId;
    }
  } catch (err) {
    console.warn("No se pudo resolver el customer_id de Zammad:", err);
  }

  return undefined;
}

/**
 * Registra automáticamente un nuevo cliente (Customer) en Zammad haciendo POST a /api/v1/users.
 * Asigna el rol "Customer" para garantizar que no consuma licencias de agente.
 */
export async function createZammadCustomer(userData: {
  email: string;
  name?: string;
  department?: string;
  organization?: string;
}): Promise<{ id: number; email: string }> {
  const systemHeaders = getSystemHeaders();
  const email = userData.email.trim().toLowerCase();

  const nameParts = (userData.name || "").trim().split(/\s+/);
  const firstname = nameParts[0] || email.split("@")[0];
  const lastname = nameParts.slice(1).join(" ") || "";

  const payload: Record<string, any> = {
    firstname,
    lastname,
    email,
    login: email,
    organization: "BID Group",
    roles: ["Customer"],
    active: true,
  };

  if (userData.department) {
    payload.department = userData.department;
  }

  const response = await fetch(`${ZAMMAD_BASE_URL}/api/v1/users`, {
    method: "POST",
    headers: systemHeaders,
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error("Error creando usuario Customer en Zammad:", response.status, errorText);
    throw new Error(`No se pudo registrar automáticamente al usuario "${email}" en Zammad: ${errorText}`);
  }

  const createdUser = await response.json();
  const newId = Number(createdUser?.id);
  if (Number.isInteger(newId) && newId > 0) {
    customerIdCache.set(email, newId);
    return { id: newId, email };
  }

  throw new Error(`Respuesta sin ID válido al registrar al usuario "${email}" en Zammad.`);
}

/**
 * Consulta la API de Zammad para verificar si un email tiene una cuenta registrada.
 * Devuelve { id, email } si el usuario existe, o null si no se encuentra.
 */
export async function getZammadUserByEmail(email: string): Promise<{ id: number; email: string } | null> {
  if (!email?.trim()) return null;
  const systemHeaders = getSystemHeaders();
  const customerId = await resolveCustomerId(email, systemHeaders);
  if (customerId) {
    return { id: customerId, email: email.trim().toLowerCase() };
  }
  return null;
}

export async function createLiveTicket(ticketData: {
  title: string;
  customer: string;
  group: string;
  body: string;
  priority?: string;
  type?: string;
  attachments?: File[];
}): Promise<{ id: number; number: string }> {
  try {
    // El token del sistema se usa para resolver la cuenta y la configuración.
    // La creación se ejecuta más abajo con la identidad efectiva del cliente.
    const systemHeaders = getSystemHeaders();
    const currentUser = getCurrentUser();
    const creatorEmail = (ticketData.customer || currentUser?.email || "").trim().toLowerCase();

    if (!creatorEmail) {
      throw new Error(
        "Debes indicar un correo electrónico válido para asociar al ticket."
      );
    }

    // 1️⃣ Buscar si el usuario ya existe en Zammad
    let customerId = await resolveCustomerId(creatorEmail, systemHeaders);

    // 2️⃣ Si NO existe en Zammad, verificar Whitelist antes de auto-crearlo
    if (!customerId) {
      if (!isEmailWhitelisted(creatorEmail)) {
        throw new Error(
          `Tu correo electrónico (${creatorEmail}) no pertenece a un dominio o correo autorizado para la creación de cuenta en la Mesa de Servicio. Solicita tu alta con el Administrador de TI.`
        );
      }

      const newCustomer = await createZammadCustomer({
        email: creatorEmail,
        name: currentUser?.name || ticketData.customer,
        department: currentUser?.department,
      });
      customerId = newCustomer.id;
    }

    // La bandeja `Users` no necesariamente está habilitada para `ticket.customer`.
    // Por eso la creación se autoriza con el token de sistema, sin impersonación.
    // La identidad funcional del solicitante se conserva mediante `customer`
    // y `article.sender: "Customer"`.
    const creationHeaders = systemHeaders;

    let priority_id = 2; // Default medium
    if (ticketData.priority) {
      const p = ticketData.priority.toLowerCase();
      if (p === "baja" || p === "low") priority_id = 1;
      else if (p === "media" || p === "medium") priority_id = 2;
      else if (p === "alta" || p === "high") priority_id = 3;
      else if (p === "crítica" || p === "critical") priority_id = 4;
    }

    // Valores reales configurados en el atributo `type` de esta instancia.
    // "Request" no es una opción válida; Zammad usa "Solicitud".
    let zammadTicketType = "Solicitud";
    if (ticketData.type) {
      const t = ticketData.type.toLowerCase();
      if (t.includes("incidente") || t === "incident") zammadTicketType = "Incident";
      else if (t.includes("requerimiento") || t === "request" || t.includes("solicitud")) zammadTicketType = "Solicitud";
      else if (t.includes("problema") || t === "problem") zammadTicketType = "Problem";
    }

    const endpoint = `${ZAMMAD_BASE_URL}/api/v1/tickets`;

    // `service` es un tree_select personalizado e independiente de los grupos.
    // Solo se envía cuando el valor existe realmente en su catálogo; por
    // ejemplo, "General" es un grupo válido pero no una categoría válida.
    const serviceTree = await fetchCategoryObjectTree();
    const selectedService = findValidServiceValue(serviceTree, ticketData.group);
    const isGeneralRequest = normalizeZammadPath(ticketData.group) === "general";
    const configuredGeneralService = isGeneralRequest
      ? findValidServiceValue(serviceTree, ZAMMAD_GENERAL_SERVICE)
      : undefined;
    const closestGeneralService = isGeneralRequest && !configuredGeneralService
      ? getServiceAncestorValues(serviceTree, ZAMMAD_GENERAL_SERVICE)[0]
      : undefined;
    const validService = selectedService
      || configuredGeneralService
      || closestGeneralService;

    // Las solicitudes generales no deben bloquearse en el frontend si una hoja
    // configurada desaparece del árbol. Zammad puede aceptar el ticket sin ese
    // atributo o aplicar su valor predeterminado. Para categorías elegidas por
    // el usuario sí se conserva la validación para evitar un enrutamiento errado.
    if (!validService && !isGeneralRequest) {
      throw new Error(
        `La categoría "${ticketData.group}" no está disponible.`
      );
    }

    // Todos los formularios entran por `Users` para que el trigger los asigne
    // según `service`; la categoría no reemplaza al grupo inicial del payload.
    const groupCandidates = await resolveTicketGroupCandidates(
      ZAMMAD_TICKET_GROUP,
      systemHeaders
    );
    const resolvedGroupStr = groupCandidates[0] || ZAMMAD_TICKET_GROUP;

    // `group` fija la bandeja de entrada del trigger y `service` conserva la
    // categoría que Zammad utiliza para asignar el grupo final.
    const attachments = await filesToZammadAttachments(ticketData.attachments);
    const ticketPayload: Record<string, any> = {
      title: ticketData.title,
      group: resolvedGroupStr,
      customer: creatorEmail,
      priority_id,
      type: zammadTicketType,
      article: {
        subject: ticketData.title,
        body: ticketData.body,
        content_type: "text/plain",
        type: "web",
        sender: "Customer",
        internal: false,
        ...(attachments.length > 0 ? { attachments } : {}),
      },
    };

    if (validService) {
      ticketPayload.service = validService;
    }

    const createTicket = () => fetch(endpoint, {
      method: "POST",
      headers: creationHeaders,
      body: JSON.stringify(ticketPayload),
    });

    let submittedService = validService || "sin categoría";
    let submittedGroup = resolvedGroupStr;
    let res: Response;
    let finalErrorText = "";

    const createWithGroupFallback = async () => {
      for (const groupCandidate of groupCandidates) {
        submittedGroup = groupCandidate;
        ticketPayload.group = groupCandidate;
        res = await createTicket();
        finalErrorText = res.ok ? "" : await res.text();

        if (res.ok || !isRetryableGroupError(res.status, finalErrorText)) {
          return;
        }
      }
    };

    await createWithGroupFallback();

    // Algunas instancias publican hojas activas en el catálogo de `service`,
    // pero su validador de creación todavía las rechaza. Solo para ese 422 se
    // intenta el ancestro válido más cercano, sin perder la selección original.
    if (validService && isInvalidServiceValueError(res.status, finalErrorText)) {
      for (const ancestorService of getServiceAncestorValues(serviceTree, validService)) {
        submittedService = ancestorService;
        ticketPayload.service = ancestorService;
        ticketPayload.article.body = `${ticketData.body}\n\nCategoría seleccionada: ${validService}`;

        await createWithGroupFallback();
        if (res.ok || !isInvalidServiceValueError(res.status, finalErrorText)) break;
      }
    }

    if (!res.ok) {
      console.error(`Zammad Ticket Creation Error [${res.status}]:`, finalErrorText);

      let zammadDetail = finalErrorText;
      try {
        const parsedError = JSON.parse(finalErrorText);
        zammadDetail = parsedError.error || parsedError.message || finalErrorText;
      } catch {
        // La respuesta no siempre es JSON; conservar el texto recibido.
      }

      const errorId = zammadDetail.match(/Error ID\s+([^:]+)/i)?.[1];
      const reference = errorId ? ` Referencia de soporte: ${errorId}.` : "";
      const readableDetail = String(zammadDetail)
        .replace(/\s+/g, " ")
        .trim()
        .slice(0, 300);
      const detail = readableDetail ? ` Detalle: ${readableDetail}` : "";
      const permissionHint = res.status === 401 || res.status === 403
        ? " Verifica que el token del sistema tenga el permiso admin.user para crear en nombre del cliente."
        : "";
      throw new Error(
        `No se pudo crear el ticket (HTTP ${res.status}). ` +
        `Grupo enviado: "${submittedGroup}". ` +
        `Categoría enviada: "${submittedService}".${reference}${detail}${permissionHint}`
      );
    }

    const createdTicket = await res.json();
    invalidateMyTicketsCache();
    return { id: createdTicket.id, number: createdTicket.number };
  } catch (err) {
    console.error("Error creating Zammad ticket:", err);
    throw err;
  }
}

interface CustomerTicketActionContext {
  customerId: number;
  headers: Record<string, string>;
  ticket: any;
}

async function getCustomerTicketActionContext(
  ticketId: number,
  customerIdentity: string
): Promise<CustomerTicketActionContext> {
  const normalizedIdentity = customerIdentity.trim();
  if (!normalizedIdentity) {
    throw new Error("No se recibió la identidad del solicitante.");
  }

  const systemHeaders = getSystemHeaders();
  const numericIdentity = Number(normalizedIdentity);
  const customerId = Number.isInteger(numericIdentity) && numericIdentity > 0
    ? numericIdentity
    : await resolveCustomerId(normalizedIdentity, systemHeaders);

  if (!customerId) {
    throw new Error("No se encontró el usuario cliente en Zammad.");
  }

  // El encabezado From obliga a evaluar el ticket desde la perspectiva del
  // solicitante, incluso si la cuenta también tiene el rol Agent.
  const headers = {
    ...systemHeaders,
    From: String(customerId),
  };
  const ticketResponse = await fetch(`${ZAMMAD_BASE_URL}/api/v1/tickets/${ticketId}`, { headers });

  if (!ticketResponse.ok) {
    const detail = await ticketResponse.text();
    throw new Error(`El solicitante no puede acceder al ticket (HTTP ${ticketResponse.status}): ${detail}`);
  }

  const ticket = await ticketResponse.json();
  if (Number(ticket.customer_id) !== customerId) {
    throw new Error("El ticket no pertenece al solicitante autenticado.");
  }

  return { customerId, headers, ticket };
}

export async function submitLiveTicketRating(
  ticketId: number,
  rating: number,
  customerIdentity: string,
  comment = "Calificado desde el portal"
): Promise<boolean> {
  try {
    if (!Number.isInteger(rating) || rating < 1 || rating > 5) return false;

    const { headers } = await getCustomerTicketActionContext(
      ticketId,
      customerIdentity
    );
    const articlesResponse = await fetch(
      `${ZAMMAD_BASE_URL}/api/v1/ticket_articles/by_ticket/${ticketId}`,
      { headers }
    );
    if (!articlesResponse.ok) {
      throw new Error(`No se pudo validar la calificación existente (HTTP ${articlesResponse.status}).`);
    }
    const articles = await articlesResponse.json();
    if (Array.isArray(articles) && getPortalRatingFromArticles(articles) !== undefined) {
      throw new Error("Este ticket ya fue calificado.");
    }

    const marker = `${PORTAL_RATING_MARKER_PREFIX}${rating}`;
    const res = await fetch(`${ZAMMAD_BASE_URL}/api/v1/ticket_articles`, {
      method: "POST",
      headers,
      body: JSON.stringify({
        ticket_id: ticketId,
        subject: "Calificación del servicio",
        body: `${comment || "Calificado desde el portal"}: ${rating}/5\n${marker}`,
        content_type: "text/plain",
        type: "web",
        sender: "Customer",
        internal: false,
      }),
    });

    if (!res.ok) {
      const detail = await res.text();
      throw new Error(`No se pudo guardar la calificación (HTTP ${res.status}): ${detail}`);
    }

    invalidateMyTicketsCache();
    return true;
  } catch (err) {
    console.error("Failed to submit ticket rating:", err);
    return false;
  }
}

export async function closeLiveTicket(
  ticketId: number,
  customerIdentity: string
): Promise<boolean> {
  try {
    const { headers } = await getCustomerTicketActionContext(ticketId, customerIdentity);
    const res = await fetch(`${ZAMMAD_BASE_URL}/api/v1/tickets/${ticketId}`, {
      method: "PUT",
      headers,
      body: JSON.stringify({ state_id: 4 }),
    });

    if (!res.ok) {
      const detail = await res.text();
      throw new Error(`No se pudo cerrar el ticket (HTTP ${res.status}): ${detail}`);
    }

    invalidateMyTicketsCache();
    return true;
  } catch (err) {
    console.error("Failed to close ticket:", err);
    return false;
  }
}

export async function addLiveTicketComment(
  ticketId: number,
  body: string,
  customerIdentity: string,
  attachments?: File[]
): Promise<boolean> {
  try {
    const { headers } = await getCustomerTicketActionContext(ticketId, customerIdentity);
    const encodedAttachments = await filesToZammadAttachments(attachments);
    const res = await fetch(`${ZAMMAD_BASE_URL}/api/v1/ticket_articles`, {
      method: "POST",
      headers,
      body: JSON.stringify({
        ticket_id: ticketId,
        subject: "Comentario desde el portal",
        body,
        content_type: "text/plain",
        type: "web",
        sender: "Customer",
        internal: false,
        ...(encodedAttachments.length > 0 ? { attachments: encodedAttachments } : {}),
      }),
    });

    if (!res.ok) {
      const detail = await res.text();
      throw new Error(`No se pudo guardar el comentario (HTTP ${res.status}): ${detail}`);
    }

    invalidateMyTicketsCache();
    return true;
  } catch (err) {
    console.error("Failed to add comment to ticket:", err);
    return false;
  }
}

export async function fetchLiveTicketAttachment(
  attachment: TicketAttachment,
  customerIdentity: string
): Promise<Blob> {
  const { headers } = await getCustomerTicketActionContext(
    attachment.ticketId,
    customerIdentity
  );
  const response = await fetch(
    `${ZAMMAD_BASE_URL}/api/v1/ticket_attachment/${attachment.ticketId}/${attachment.articleId}/${attachment.id}`,
    { headers }
  );

  if (!response.ok) {
    throw new Error(
      `No se pudo descargar "${attachment.filename}" (HTTP ${response.status}).`
    );
  }

  return response.blob();
}

export interface ZammadCategoryTreeNode {
  name: string;
  value: string;
  children?: ZammadCategoryTreeNode[];
}

const CATEGORY_TREE_CACHE_TTL = 5 * 60 * 1000;
let categoryTreeCache: { data: ZammadCategoryTreeNode[]; fetchedAt: number } | null = null;
let categoryTreeRequest: Promise<ZammadCategoryTreeNode[]> | null = null;

async function loadCategoryObjectTree(): Promise<ZammadCategoryTreeNode[]> {
  try {
    const headers = getSystemHeaders();
    const res = await fetch(`${ZAMMAD_BASE_URL}/api/v1/object_manager_attributes`, { headers });
    if (!res.ok) return [];
    const data = await res.json();

    // El ID cambia entre QA y producción. Resolver siempre por el nombre
    // estable/configurable del atributo y nunca por un ID de una instancia.
    if (Array.isArray(data)) {
      const categoryAttribute = data.find(
        (attr: any) => attr?.active !== false && attr?.name === ZAMMAD_CATEGORY_ATTRIBUTE
      );
      if (Array.isArray(categoryAttribute?.data_option?.options)) {
        return categoryAttribute.data_option.options;
      }
    }

    return [];
  } catch (err) {
    console.error(`Failed to fetch category attribute '${ZAMMAD_CATEGORY_ATTRIBUTE}':`, err);
    return [];
  }
}

export async function fetchLiveMyTickets(options?: MyTicketsOptions): Promise<ZammadUserTicket[]> {
  const cacheKey = `${options?.userId || ""}:${(options?.email || "").trim().toLowerCase()}`;
  const cached = myTicketsCache.get(cacheKey);
  if (!options?.forceRefresh && cached && Date.now() - cached.fetchedAt < MY_TICKETS_CACHE_TTL) {
    return cached.data;
  }

  if (!options?.forceRefresh) {
    const pending = myTicketsRequests.get(cacheKey);
    if (pending) return pending;
  }

  const request = loadLiveMyTickets(options)
    .then((data) => {
      myTicketsCache.set(cacheKey, { data, fetchedAt: Date.now() });
      return data;
    })
    .finally(() => {
      if (myTicketsRequests.get(cacheKey) === request) {
        myTicketsRequests.delete(cacheKey);
      }
    });

  myTicketsRequests.set(cacheKey, request);
  return request;
}

export async function fetchCategoryObjectTree(): Promise<ZammadCategoryTreeNode[]> {
  const now = Date.now();
  if (categoryTreeCache && now - categoryTreeCache.fetchedAt < CATEGORY_TREE_CACHE_TTL) {
    return categoryTreeCache.data;
  }
  if (categoryTreeRequest) return categoryTreeRequest;

  categoryTreeRequest = loadCategoryObjectTree()
    .then((data) => {
      categoryTreeCache = { data, fetchedAt: Date.now() };
      return data;
    })
    .finally(() => {
      categoryTreeRequest = null;
    });

  return categoryTreeRequest;
}

export async function fetchLiveGroups(_options?: {
  userId?: string;
  email?: string;
}): Promise<{
  groups: { id: number; name: string; active: boolean }[];
  permittedGroupIds?: number[];
  categoryTree?: ZammadCategoryTreeNode[];
  rawResponse: any;
  error?: string;
}> {
  try {
    // Consultar las categorías por nombre de atributo; su ID varía por ambiente.
    const categoryTree = await fetchCategoryObjectTree();

    const rootNodes = categoryTree || [];
    const simulatedGroups = rootNodes.map((cat, idx) => ({
      id: idx + 1,
      name: cat.name,
      active: true,
    }));
    const permittedGroupIds = simulatedGroups.map((g) => g.id);

    return {
      groups: simulatedGroups,
      permittedGroupIds,
      categoryTree,
      rawResponse: categoryTree,
      error: categoryTree.length === 0 ? "No se encontraron categorías disponibles" : undefined,
    };
  } catch (err) {
    console.error("Fetch live categories failed:", err);
    return {
      groups: [],
      rawResponse: null,
      error: err instanceof Error ? err.message : "Error desconocido al consultar los datos",
    };
  }
}

// ─── COMPATIBILIDAD CON VISTAS LEGACY ───

export async function fetchMyTickets(userId: string): Promise<ZammadUserTicket[]> {
  return fetchLiveMyTickets({ userId });
}

export async function fetchStats(): Promise<ZammadTicketStats> {
  return fetchLiveStats();
}

export async function syncTicketStatus(ticketId: string): Promise<{ status: string; updatedAt: string }> {
  const tickets = await fetchLiveMyTickets({});
  const ticket = tickets.find((t) => t.id === ticketId);
  if (!ticket) throw new Error("Ticket not found");
  return { status: ticket.status, updatedAt: ticket.updatedAt };
}
